
import React from "react";
import { AlertTriangle, Camera, VideoOff, RefreshCcw, User, AlertCircle, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const CameraPermissionDialog = ({ 
  open, 
  onPermissionGranted, 
  onPermissionDenied, 
  initializing, 
  required = false 
}) => (
  <Dialog open={open} onOpenChange={(open) => {
    // Only allow closing if not required
    if (!open && !required) {
      onPermissionDenied();
    }
  }}>
    <DialogContent hideCloseButton={true}>
      <DialogHeader>
        <DialogTitle>Camera Permission Required</DialogTitle>
        <DialogDescription>
          This exam requires camera access for proctoring. Please click "Allow Camera Access" to proceed with the exam.
        </DialogDescription>
      </DialogHeader>
      <div className="flex justify-center py-6">
        <Camera className="h-16 w-16 text-primary" />
      </div>
      <DialogFooter>
        <Button onClick={onPermissionGranted} disabled={initializing}>
          {initializing ? "Accessing Camera..." : "Allow Camera Access"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const FaceNotDetectedDialog = ({ 
  open, 
  onRetryCamera, 
  retryingCamera 
}) => (
  <Dialog open={open} onOpenChange={() => {}}>
    <DialogContent hideCloseButton={true}>
      <DialogHeader>
        <DialogTitle className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          Face Not Detected
        </DialogTitle>
        <DialogDescription>
          Your face is not visible to the camera. Please adjust your position to ensure your face is clearly visible.
          <div className="mt-4 text-red-500">
            You cannot proceed with the exam until your face is detected.
          </div>
        </DialogDescription>
      </DialogHeader>
      <div className="flex justify-center py-4">
        <User className="h-16 w-16 text-red-500" />
      </div>
      <DialogFooter>
        <Button onClick={onRetryCamera} disabled={retryingCamera}>
          {retryingCamera ? "Reconnecting Camera..." : "Retry Camera Connection"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ScreenCapturePermissionDialog = ({ 
  open, 
  onPermissionGranted, 
  onPermissionDenied, 
  initializing, 
  required = false 
}) => (
  <Dialog open={open} onOpenChange={(open) => {
    // Only allow closing if not required
    if (!open && !required) {
      onPermissionDenied();
    }
  }}>
    <DialogContent hideCloseButton={true} className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="text-xl">🖥️ Screen Sharing Permission Required</DialogTitle>
        <DialogDescription className="space-y-4">
          <p>This exam requires screen sharing for proctoring. When you click "Allow Screen Sharing", your browser will show a dialog.</p>
          
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-red-800 mb-2">CRITICAL: You MUST select "Entire screen"</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>✅ <strong>DO</strong> select "Entire screen" option</li>
                  <li>❌ <strong>DO NOT</strong> select "Chrome tab"</li>
                  <li>❌ <strong>DO NOT</strong> select "Window"</li>
                  <li>❌ <strong>DO NOT</strong> click "Cancel"</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">What to expect:</h4>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Click "Allow Screen Sharing" below</li>
              <li>Browser will show screen sharing options</li>
              <li>Select the "Entire screen" tab</li>
              <li>Choose your main screen from the preview</li>
              <li>Click "Share" (NOT Cancel)</li>
            </ol>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> If you select the wrong option, you'll be asked to try again. 
              This ensures exam integrity and security.
            </p>
          </div>
        </DialogDescription>
      </DialogHeader>
      
      <div className="flex justify-center py-4">
        <div className="text-center">
          <Monitor className="h-20 w-20 text-primary mx-auto mb-2" />
          <p className="text-sm text-gray-600">Your entire screen will be monitored</p>
        </div>
      </div>
      
      <DialogFooter className="flex-col space-y-2">
        <Button 
          onClick={onPermissionGranted} 
          disabled={initializing}
          className="w-full bg-green-600 hover:bg-green-700"
          size="lg"
        >
          {initializing ? (
            <>
              <RefreshCcw className="h-4 w-4 mr-2 animate-spin" />
              Setting up screen sharing...
            </>
          ) : (
            <>
              <Monitor className="h-4 w-4 mr-2" />
              Allow Screen Sharing (Entire Screen Only)
            </>
          )}
        </Button>
        
        {!required && (
          <Button 
            variant="outline" 
            onClick={onPermissionDenied}
            className="w-full"
          >
            Skip Screen Capture
          </Button>
        )}
        
        <p className="text-xs text-gray-500 text-center mt-2">
          Screen sharing is required for exam proctoring and security
        </p>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const WarningDialog = ({ 
  open, 
  onAcknowledge, 
  warningMessage, 
  warningCount, 
  warningsAllowed
}) => (
  <AlertDialog open={open} onOpenChange={(open) => !open && onAcknowledge()}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center">
          <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
          Warning {warningCount}/{warningsAllowed || "∞"}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {warningMessage}
          {warningsAllowed > 0 && warningCount + 1 >= warningsAllowed && (
            <div className="mt-2 text-red-500 font-semibold">
              This is your last warning. Any further violations will result
              in disqualification.
            </div>
          )}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogAction onClick={onAcknowledge}>
          I Understand
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
